import "./textMessage.css";
import { useState, useEffect, useRef } from "react";
import * as ConversationEntryUtil from "../helpers/conversationEntryUtil";
import { util } from "../helpers/common";
import { localizeAgentText } from "../awac/agentTextL10n";
import { t } from "../awac/i18n";

// Streaming-style reveal tuning. Only fresh incoming agent messages stream;
// replayed history (session restore) renders instantly.
const STREAM_FRESHNESS_MS = 5000;   // message must be younger than this to stream
const STREAM_MAX_DURATION_MS = 1200; // total reveal never exceeds this
const STREAM_MS_PER_CHAR = 12;       // short messages reveal proportionally faster

/**
 * Whether this entry should get the progressive text reveal.
 * @param {object} conversationEntry
 * @returns {boolean}
 */
function shouldStreamEntry(conversationEntry) {
    if (conversationEntry.isEndUserMessage) {
        return false;
    }
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return false;
    }
    const ts = Number(conversationEntry.transcriptedTimestamp);
    if (!ts || Number.isNaN(ts)) {
        return false;
    }
    return (Date.now() - ts) < STREAM_FRESHNESS_MS;
}

export default function TextMessage({conversationEntry}) {
    // Initialize acknowledgement status.
    let [isSent, setIsSent] = useState(false);
    let [isDelivered, setIsDelivered] = useState(false);
    let [isRead, setIsRead] = useState(false);
    let [acknowledgementTimestamp, setAcknowledgementTimestamp] = useState('');

    const fullText = localizeAgentText(ConversationEntryUtil.getTextMessageContent(conversationEntry) || "", conversationEntry.isEndUserMessage);
    // Progressive reveal state — starts hidden only for fresh agent messages.
    const streamRef = useRef(shouldStreamEntry(conversationEntry));
    const [revealedChars, setRevealedChars] = useState(streamRef.current ? 0 : fullText.length);

    // Animate the reveal with requestAnimationFrame; runs once per entry.
    useEffect(() => {
        if (!streamRef.current || !fullText.length) {
            return undefined;
        }
        const duration = Math.min(STREAM_MAX_DURATION_MS, Math.max(200, fullText.length * STREAM_MS_PER_CHAR));
        const start = performance.now();
        let rafId;
        function step(now) {
            const progress = Math.min(1, (now - start) / duration);
            setRevealedChars(Math.ceil(progress * fullText.length));
            if (progress < 1) {
                rafId = requestAnimationFrame(step);
            }
        }
        rafId = requestAnimationFrame(step);
        return () => cancelAnimationFrame(rafId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (conversationEntry.isRead) {
            setIsRead(conversationEntry.isRead);
            setAcknowledgementTimestamp(conversationEntry.readAcknowledgementTimestamp);
        } else if (conversationEntry.isDelivered) {
            setIsDelivered(conversationEntry.isDelivered);
            setAcknowledgementTimestamp(conversationEntry.deliveryAcknowledgementTimestamp);
        } else if (conversationEntry.isSent) {
            setIsSent(conversationEntry.isSent);
            setAcknowledgementTimestamp(conversationEntry.transcriptedTimestamp);
        }
    }, [conversationEntry]);

    /**
     * Generates a classname for Text Message metadata such as sender text.
     * @returns {string}
     */
    function generateMessageSenderContentClassName() {
        const className = `textMessageSenderContent ${conversationEntry.isEndUserMessage ? `outgoing` : `incoming`}`;

        return className;
    }

    /**
     * Generates a classname for Text Message bubble container.
     * @returns {string}
     */
    function generateMessageBubbleContainerClassName() {
        const className = `textMessageBubbleContainer`;

        return className;
    }

    /**
     * Generates a classname for Text Message bubble ui.
     * @returns {string}
     */
    function generateMessageBubbleClassName() {
        const className = `textMessageBubble ${conversationEntry.isEndUserMessage ? `outgoing` : `incoming`}`;

        return className;
    }

    /**
     * Generates a classname for Text Message content (i.e. actual text).
     * @returns {string}
     */
    function generateMessageContentClassName() {
        const className = `textMessageContent`;

        return className;
    }

    /**
     * Generates a text with the message sender infomation.
     * @returns {string}
     */
    function generateMessageSenderContentText() {
        const formattedTime = util.getFormattedTime(conversationEntry.transcriptedTimestamp);

        return t("chat.senderAt", { name: conversationEntry.isEndUserMessage ? t("chat.you") : conversationEntry.actorName, time: formattedTime });
    }

    /**
     * Generates text content with the message acknowledgement infomation.
     * @returns {string}
     */
    function generateMessageAcknowledgementContentText() {
        const formattedAcknowledgementTimestamp = util.getFormattedTime(acknowledgementTimestamp);

        if (conversationEntry.isEndUserMessage) {
            if (isRead) {
                return t("chat.readAt", { time: formattedAcknowledgementTimestamp });
            } else if (isDelivered) {
                return t("chat.deliveredAt", { time: formattedAcknowledgementTimestamp });
            } else if (isSent) {
                return t("chat.sent");
            } else {
                return ``;
            }
        }
    }

    return (
        <>
            <div className={generateMessageBubbleContainerClassName()}>
                <div className={generateMessageBubbleClassName()}>
                    <p className={generateMessageContentClassName()} aria-label={fullText}>
                        <span aria-hidden={streamRef.current && revealedChars < fullText.length ? "true" : undefined}>
                            {streamRef.current ? fullText.slice(0, revealedChars) : fullText}
                        </span>
                    </p>
                </div>
            </div>
            <p className={generateMessageSenderContentClassName()}>{generateMessageAcknowledgementContentText()}{generateMessageSenderContentText()}</p>
        </>
    );
}